import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getAiStatus,
  getEventAi,
  analyzeEventAi,
  getAnalyzeStatus,
  askEventAi,
  acceptAiSuggestion,
  dismissAiSuggestion,
  rateAiSuggestion,
  getAiAttention,
  getAiConfig,
  updateAiConfig,
  getAiTelemetry,
  runAiMaintenance,
  runAiSweepTick,
  runSalesSync,
} from '../api';
import { inventoryKeys } from './useInventory';
import { mergeFeedbackSummary } from '../utils/aiFeedback';

export const aiKeys = {
  all: ['ai'],
  status: ['ai', 'status'],
  event: (eventId) => ['ai', 'event', eventId],
  attention: ['ai', 'attention'],
  config: ['ai', 'config'],
  telemetry: ['ai', 'telemetry'],
};

// Compare opens in its own browser tab, so invalidation never crosses tabs.
// AI reads are SQLite-cheap: short staleTime + refetch-on-focus keeps the
// toggle, suggestions and flags fresh everywhere (same pattern as useNotes).
const CROSS_TAB_FRESHNESS = {
  staleTime: 60 * 1000,
  refetchOnWindowFocus: true,
  retry: 1,
};

export function useAiStatus(options = {}) {
  return useQuery({
    queryKey: aiKeys.status,
    queryFn: async () => (await getAiStatus()).data,
    ...CROSS_TAB_FRESHNESS,
    ...options,
  });
}

// Everything Compare needs for one event: enabled flag, summary, suggestions,
// flags, timestamps. Returns { enabled:false } when the assistant is off, so
// callers gate on data?.enabled and render nothing otherwise.
export function useEventAi(eventId, options = {}) {
  return useQuery({
    queryKey: aiKeys.event(eventId),
    queryFn: async () => (await getEventAi(eventId)).data,
    enabled: !!eventId,
    ...CROSS_TAB_FRESHNESS,
    ...options,
  });
}

// "Analyze now" / "Price with AI" — long-running. The POST returns 202 at
// once (the run outlives nginx's 60 s proxy timeout); we then poll the status
// endpoint every few seconds until it reports done or error, and write the
// fresh assist data straight into the cache. Resolves to { data } so callers
// keep reading res.data as before.
const POLL_FIRST_MS = 1000;
const POLL_MS = 3000;
const POLL_MAX_MS = 12 * 60 * 1000;
const sleep = (ms) => new Promise(r => setTimeout(r, ms));

// What the server is doing right now, in words. `progress` comes from the
// status poll: { phase, phaseDetail, startedAt, elapsedMs }.
const PHASE_LABELS = {
  starting: 'starting', comps: 'fetching comparables', invoices: 'fetching invoices', history: 'syncing price history',
  deterministic: 'computing metrics', 'sold-data': 'pulling sold data', whatif: 'asking Skybox for L prices',
  analysis: 'analyzing', verify: 'verifying', persist: 'saving', done: 'finishing',
};
export function progressLabel(progress, now = Date.now()) {
  if (!progress) return '';
  let s = PHASE_LABELS[progress.phase] || progress.phase || '';
  const d = progress.phaseDetail;
  if (d && Number.isFinite(d.n) && Number.isFinite(d.i)) s += ` ${d.i}/${d.n}`;
  else if (d && Number.isFinite(d.outputTokens)) s += ` (${d.outputTokens.toLocaleString()} tokens)`;
  if (progress.startedAt) {
    const secs = Math.max(0, Math.floor((now - new Date(progress.startedAt).getTime()) / 1000));
    s += ` · ${Math.floor(secs / 60)}:${String(secs % 60).padStart(2, '0')}`;
  }
  return s;
}

export function useAnalyzeEvent(eventId) {
  const queryClient = useQueryClient();
  const [progress, setProgress] = useState(null);
  const mutation = useMutation({
    mutationFn: async (directive) => {
      setProgress({ phase: 'starting', phaseDetail: null, startedAt: new Date().toISOString() });
      await analyzeEventAi(eventId, directive); // 202 { started } — or { running } if one is already going
      const deadline = Date.now() + POLL_MAX_MS;
      let misses = 0, first = true;
      while (Date.now() < deadline) {
        await sleep(first ? POLL_FIRST_MS : POLL_MS);
        first = false;
        let s;
        try {
          s = (await getAnalyzeStatus(eventId)).data;
          misses = 0;
        } catch (err) {
          // a flaky poll is not a failed analysis — the job is still running server-side
          if (++misses >= 5) throw err;
          continue;
        }
        if (s.phase) setProgress({ phase: s.phase, phaseDetail: s.phaseDetail, startedAt: s.startedAt });
        if (s.status === 'done') return { data: s };
        if (s.status === 'error') { const e = new Error(s.error || 'Analysis failed'); e.code = s.code; throw e; }
        if (s.status === 'idle') throw new Error('The analysis ended without a result (server restarted?) — reload the page to check');
      }
      throw new Error('Still running after 12 minutes — reload the page later; the result appears when the run finishes');
    },
    onSuccess: (res) => {
      const { status, skipped, reason, runId, cost, verified, startedAt, finishedAt, phase, phaseDetail, phaseAt, elapsedMs, timing, ...assist } = res.data;
      queryClient.setQueryData(aiKeys.event(eventId), assist);
      queryClient.invalidateQueries({ queryKey: aiKeys.attention });
      queryClient.invalidateQueries({ queryKey: aiKeys.status });
    },
    onSettled: () => setProgress(null),
  });
  return { ...mutation, progress };
}

export function useAskEvent(eventId) {
  return useMutation({
    mutationFn: (question) => askEventAi(eventId, question),
  });
}

// Patch one suggestion (by id) wherever the event payload carries it: the
// live suggestions[] (pending + stale) and latestByInventory (newest per
// listing, any status — absent until the backend sends it).
function patchSuggestion(old, id, fn) {
  if (!old) return old;
  let next = old;
  if (Array.isArray(old.suggestions) && old.suggestions.some(s => s.id === id)) {
    next = { ...next, suggestions: old.suggestions.map(s => (s.id === id ? fn(s) : s)) };
  }
  const latest = old.latestByInventory;
  if (latest && typeof latest === 'object') {
    const key = Object.keys(latest).find(k => latest[k]?.id === id);
    if (key) next = { ...next, latestByInventory: { ...latest, [key]: fn(latest[key]) } };
  }
  return next;
}

// Accept (as-is or with a modified price). Price-cache bookkeeping
// (eventPricing setQueryData, confirmedPriceUpdates ref) stays in Compare's
// applyPriceResult because it owns those refs; here we only refresh AI data
// and the Dashboard inventory. NEVER invalidate eventPricing from here — the
// server would refetch Skybox.
export function useAcceptSuggestion(eventId) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, price, vividListingId, zone }) => acceptAiSuggestion(id, { price, vividListingId, zone }),
    onSuccess: (res, variables) => {
      queryClient.setQueryData(aiKeys.event(eventId), (old) => patchSuggestion(old, variables.id,
        s => ({ ...s, status: res.data.status || 'accepted', appliedPrice: res.data.newPrice })));
      queryClient.invalidateQueries({ queryKey: aiKeys.event(eventId) });
      queryClient.invalidateQueries({ queryKey: aiKeys.attention });
      queryClient.invalidateQueries({ queryKey: inventoryKeys.all });
    },
  });
}

// Optimistic dismiss: the cell disappears immediately; rollback on error.
// { id, reasonCode?, note?, source? } — with a reasonCode the server also
// stores it as this user's "wrong" feedback and returns that FeedbackView.
export function useDismissSuggestion(eventId) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reasonCode, note, source }) => dismissAiSuggestion(id, { reasonCode, note, source }),
    onMutate: async ({ id, reasonCode }) => {
      await queryClient.cancelQueries({ queryKey: aiKeys.event(eventId) });
      const previous = queryClient.getQueryData(aiKeys.event(eventId));
      queryClient.setQueryData(aiKeys.event(eventId), (old) => patchSuggestion(old, id,
        s => ({ ...s, status: 'dismissed', ...(reasonCode ? { dismissReason: reasonCode } : {}) })));
      return { previous };
    },
    onSuccess: (res, variables) => {
      const view = res?.data?.feedback;
      if (view) {
        queryClient.setQueryData(aiKeys.event(eventId), (old) => patchSuggestion(old, variables.id,
          s => ({ ...s, feedback: mergeFeedbackSummary(s.feedback, view) })));
        queryClient.invalidateQueries({ queryKey: aiKeys.telemetry });
      }
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) queryClient.setQueryData(aiKeys.event(eventId), context.previous);
    },
    onSettled: () => {
      // latestByInventory (the admin "dismissed · Rate" chip) comes from the server
      queryClient.invalidateQueries({ queryKey: aiKeys.event(eventId) });
      queryClient.invalidateQueries({ queryKey: aiKeys.attention });
    },
  });
}

// Rate a suggestion (any status). Feedback only — nothing is written to
// Skybox and the suggestion's status never changes. Patches `feedback` into
// both suggestions[] and latestByInventory, then refetches.
export function useRateSuggestion(eventId) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, verdict, reasonCode, preferredPrice, note }) =>
      rateAiSuggestion(id, { verdict, reasonCode, preferredPrice, note }),
    onSuccess: (res, variables) => {
      const view = res?.data?.feedback;
      if (view) {
        queryClient.setQueryData(aiKeys.event(eventId), (old) => patchSuggestion(old, variables.id,
          s => ({ ...s, feedback: mergeFeedbackSummary(s.feedback, view) })));
      }
      queryClient.invalidateQueries({ queryKey: aiKeys.event(eventId) });
      queryClient.invalidateQueries({ queryKey: aiKeys.telemetry });
    },
  });
}

export function useAiAttention(options = {}) {
  return useQuery({
    queryKey: aiKeys.attention,
    queryFn: async () => (await getAiAttention()).data,
    ...CROSS_TAB_FRESHNESS,
    ...options,
  });
}

// ---- Admin --------------------------------------------------------------------

export function useAiConfig(options = {}) {
  return useQuery({
    queryKey: aiKeys.config,
    queryFn: async () => (await getAiConfig()).data,
    staleTime: 30 * 1000,
    ...options,
  });
}

export function useUpdateAiConfig() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (updates) => updateAiConfig(updates),
    onSuccess: () => {
      // The toggle changes what every AI query returns
      queryClient.invalidateQueries({ queryKey: aiKeys.all });
    },
  });
}

export function useAiTelemetry(options = {}) {
  return useQuery({
    queryKey: aiKeys.telemetry,
    queryFn: async () => (await getAiTelemetry()).data,
    staleTime: 60 * 1000,
    ...options,
  });
}

export function useRunAiMaintenance() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => runAiMaintenance(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: aiKeys.all });
    },
  });
}

export function useRunAiSweepTick() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => runAiSweepTick(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: aiKeys.all });
    },
  });
}

// Admin "Run now" for the sales sync (one tick)
export function useRunSalesSync() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => runSalesSync(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: aiKeys.telemetry });
      queryClient.invalidateQueries({ queryKey: aiKeys.status });
    },
  });
}
